import { ExternalProviderFormDialog } from '@/components/molecules/ExternalProviderFormDialog';
import { useTRPC } from '@/utils/trpc';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { Button } from '@liza/ui/components/ui/button';
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@liza/ui/components/ui/combobox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@liza/ui/components/ui/dialog';
import { Field, FieldLabel } from '@liza/ui/components/ui/field';
import { Spinner } from '@liza/ui/components/ui/spinner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

type ExternalCareTeamMember = PatientAdminApiRouterOutputs['externalCareTeamMember']['listForOrg']['members'][number];

/** Sentinel id for the synthetic "Create «name»" dropdown row; never a real role id. */
const CREATE_MEMBER_ID = '__create-external-provider__';

/** onInputValueChange reasons that reflect user edits — everything else is the primitive's own resets (e.g. snapping the input back on blur), which must not clobber typed free text. */
const USER_INPUT_REASONS = new Set(['input-change', 'input-clear', 'clear-press', 'item-press']);

type PickerItem = {
  id: string;
  label: string;
  description?: string;
  member?: ExternalCareTeamMember;
};

const toPickerItem = (member: ExternalCareTeamMember): PickerItem => ({
  id: member.roleId,
  label: member.name,
  description: member.organizationName,
  member,
});

export type LinkExternalProviderDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  patientId: string;
  /** Role ids already actively linked to the patient — excluded from the picker. */
  linkedRoleIds: string[];
};

/**
 * Links an org-level external provider to the patient's care team: pick one from the
 * org's directory, or create a new one and link it in the same flow.
 */
export const LinkExternalProviderDialog = ({
  open,
  onOpenChange,
  organizationId,
  patientId,
  linkedRoleIds,
}: LinkExternalProviderDialogProps): React.ReactNode => {
  const { t } = useTranslation('common');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<PickerItem | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  // The picker filters the org directory client-side (listForOrg has no term search);
  // the page cap comfortably exceeds today's directory sizes.
  const membersQuery = useQuery(
    trpc.externalCareTeamMember.listForOrg.queryOptions(
      { organizationId, pageIndex: 0, pageSize: 100 },
      { enabled: open },
    ),
  );

  const linkMutation = useMutation(
    trpc.externalCareTeamMember.linkToPatient.mutationOptions({
      onSuccess: () => {
        toast.success(t('externalProviderLinked'));
        void queryClient.invalidateQueries({ queryKey: trpc.externalCareTeamMember.listForPatient.queryKey() });
        onOpenChange(false);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  useEffect(() => {
    if (open) {
      setSelected(null);
      setInputValue('');
    }
  }, [open]);

  const members = membersQuery.data?.members ?? [];
  const available = members.filter((member) => member.active && !linkedRoleIds.includes(member.roleId));
  const trimmedInput = inputValue.trim();
  const hasExactMatch = available.some((member) => member.name.toLowerCase() === trimmedInput.toLowerCase());
  const items: PickerItem[] = [
    ...available.map(toPickerItem),
    ...(trimmedInput && !hasExactMatch ? [{ id: CREATE_MEMBER_ID, label: trimmedInput }] : []),
  ];

  const selectedMember = selected?.member;

  const handleLink = (): void => {
    if (!selectedMember) return;
    const typeCode = selectedMember.typeCode;
    linkMutation.mutate({
      patientId,
      roleId: selectedMember.roleId,
      // The participant role defaults to the member's practitioner type.
      role: typeCode?.code ? { system: typeCode.system, code: typeCode.code, display: typeCode.display } : undefined,
    });
  };

  const handleCreated = (member: ExternalCareTeamMember): void => {
    setSelected(toPickerItem(member));
    setInputValue(member.name);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('addExternalProvider')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <Field>
              <FieldLabel htmlFor="link-external-provider-member">{t('externalProvider')}</FieldLabel>
              <Combobox<PickerItem>
                items={items}
                value={selected}
                onValueChange={(item) => {
                  if (item?.id === CREATE_MEMBER_ID) {
                    setSelected(null);
                    setCreateOpen(true);
                    return;
                  }
                  setSelected(item ?? null);
                  if (item) setInputValue(item.label);
                }}
                inputValue={inputValue}
                onInputValueChange={(value, details) => {
                  if (!USER_INPUT_REASONS.has(details.reason)) return;
                  setInputValue(value);
                  if (selected && value !== selected.label) setSelected(null);
                }}
                itemToStringLabel={(item) => item.label}
                itemToStringValue={(item) => item.id}
                isItemEqualToValue={(a, b) => a.id === b.id}
              >
                <ComboboxInput
                  id="link-external-provider-member"
                  data-testid="link-external-provider-member"
                  aria-label={t('externalProvider')}
                  aria-required
                  disabled={linkMutation.isPending}
                />
                <ComboboxContent className="pointer-events-auto" onWheel={(e) => e.stopPropagation()}>
                  <ComboboxList>
                    <ComboboxEmpty>
                      {membersQuery.isLoading ? <Spinner className="size-4" /> : t('noExternalProvidersFound')}
                    </ComboboxEmpty>
                    <ComboboxCollection>
                      {(item) => (
                        <ComboboxItem key={item.id} value={item}>
                          {item.id === CREATE_MEMBER_ID ? (
                            t('createExternalProviderOption', { name: item.label })
                          ) : (
                            <div className="flex flex-col">
                              <span>{item.label}</span>
                              {item.description && (
                                <span className="text-xs text-muted-foreground">{item.description}</span>
                              )}
                            </div>
                          )}
                        </ComboboxItem>
                      )}
                    </ComboboxCollection>
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </Field>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-link-external-provider-button"
            >
              {t('cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleLink}
              disabled={!selectedMember || linkMutation.isPending}
              data-testid="submit-link-external-provider-button"
            >
              {linkMutation.isPending ? <Spinner className="mr-2 size-4" /> : null}
              {t('add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ExternalProviderFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        organizationId={organizationId}
        mode="create"
        onCreated={handleCreated}
      />
    </>
  );
};
